import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { LuArrowRight, LuChartLine, LuCircleAlert, LuClipboardList, LuClock, LuFileWarning } from 'react-icons/lu'
import { useDashboardInsights } from '../../hooks/useDashboardInsights'
import { formatIdr } from './CurrencyText'
import classes from './InsightStrip.module.css'

type Tone = 'neutral' | 'success' | 'warning' | 'urgent'

interface Card {
  key: string
  icon: React.ReactNode
  title: string
  value: string
  sub: string
  cta: string
  tone: Tone
  href: string
}

/**
 * Top-of-dashboard insight strip — replaces the static KPI grid.
 * Each card surfaces something the operator can act on right now:
 * pending requests, overdue invoices, late check-outs, today's
 * activity, revenue trend, expiring contracts.
 *
 * Cards adopt visual urgency (red/orange/teal/neutral) based on the
 * underlying data, so the eye is drawn first to what needs attention.
 */
export function InsightStrip() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data } = useDashboardInsights()

  if (!data) return <div className={classes.grid}>{/* skeleton placeholder, kept silent until data lands */}</div>

  const cards: Card[] = []

  // 1. Pending requests
  const pendingTotal = data.pending.requests + data.pending.waitlisted
  cards.push({
    key: 'pending',
    icon: <LuClipboardList size={14} />,
    title: t('page.overview.insight_pending_title'),
    value: String(pendingTotal),
    sub:
      pendingTotal === 0
        ? t('page.overview.insight_pending_empty')
        : pendingTotal === 1
          ? t('page.overview.insight_pending_sub_one', { n: pendingTotal })
          : t('page.overview.insight_pending_sub_plural', { n: pendingTotal }),
    cta: pendingTotal > 0 ? t('page.overview.insight_pending_cta') : '',
    tone: pendingTotal > 5 ? 'urgent' : pendingTotal > 0 ? 'warning' : 'success',
    href: '/dashboard/reservations/requests',
  })

  // 2. Overdue invoices
  cards.push({
    key: 'overdue',
    icon: <LuCircleAlert size={14} />,
    title: t('page.overview.insight_overdue_title'),
    value: data.overdue.count === 0 ? '0' : formatIdr(data.overdue.amountIdr).replace(/^Rp\s*/, ''),
    sub:
      data.overdue.count === 0
        ? t('page.overview.insight_overdue_empty')
        : data.overdue.count === 1
          ? t('page.overview.insight_overdue_sub_one', {
              n: data.overdue.count,
              amount: formatIdr(data.overdue.amountIdr),
            })
          : t('page.overview.insight_overdue_sub_plural', {
              n: data.overdue.count,
              amount: formatIdr(data.overdue.amountIdr),
            }),
    cta: data.overdue.count > 0 ? t('page.overview.insight_overdue_cta') : '',
    tone: data.overdue.count > 0 ? 'urgent' : 'success',
    href: '/dashboard/billing/invoices?tab=OVERDUE',
  })

  // 3. Late check-outs
  const lateCount = data.lateCheckouts.length
  const topLate = data.lateCheckouts[0]
  const lateSub =
    lateCount === 0
      ? t('page.overview.insight_late_empty')
      : topLate
        ? t('page.overview.insight_late_detail', {
            customer: topLate.customerName ?? '—',
            boat: topLate.boatName ?? '—',
            hours: Math.max(1, Math.round(topLate.minutesLate / 60)),
          })
        : lateCount === 1
          ? t('page.overview.insight_late_sub_one', { n: lateCount })
          : t('page.overview.insight_late_sub_plural', { n: lateCount })
  cards.push({
    key: 'late',
    icon: <LuClock size={14} />,
    title: t('page.overview.insight_late_title'),
    value: String(lateCount),
    sub: lateSub,
    cta: lateCount > 0 && topLate ? t('page.overview.insight_late_cta') : '',
    tone: lateCount > 0 ? 'urgent' : 'success',
    href: topLate ? `/dashboard/reservations/${topLate.id}` : '/dashboard/reservations/bookings?tab=CHECKED_IN',
  })

  // 4. Today's activity
  const todayTotal = data.today.arrivals + data.today.departures
  cards.push({
    key: 'today',
    icon: <LuArrowRight size={14} />,
    title: t('page.overview.insight_today_title'),
    value: String(todayTotal),
    sub: t('page.overview.insight_today_sub', {
      arrivals: data.today.arrivals,
      departures: data.today.departures,
    }),
    cta: t('page.overview.insight_today_cta'),
    tone: todayTotal > 0 ? 'neutral' : 'success',
    href: '/dashboard/occupancy/arrivals',
  })

  // 5. Revenue trend
  const momPct = data.revenue.momPct
  let momLine: string
  if (momPct === null) momLine = t('page.overview.insight_revenue_mom_none')
  else if (momPct === 0) momLine = t('page.overview.insight_revenue_mom_flat')
  else if (momPct > 0) momLine = t('page.overview.insight_revenue_mom_up', { n: momPct })
  else momLine = t('page.overview.insight_revenue_mom_down', { n: Math.abs(momPct) })
  cards.push({
    key: 'revenue',
    icon: <LuChartLine size={14} />,
    title: t('page.overview.insight_revenue_title'),
    value: formatIdr(data.revenue.thisMonthIdr).replace(/^Rp\s*/, ''),
    sub: momLine,
    cta: t('page.overview.insight_revenue_cta'),
    tone: momPct !== null && momPct < 0 ? 'warning' : 'neutral',
    href: '/dashboard/analytics?tab=revenue',
  })

  // 6. Expiring contracts
  const expCount = data.expiringContracts.length
  cards.push({
    key: 'expiring',
    icon: <LuFileWarning size={14} />,
    title: t('page.overview.insight_expiring_title'),
    value: String(expCount),
    sub:
      expCount === 0
        ? t('page.overview.insight_expiring_empty')
        : expCount === 1
          ? t('page.overview.insight_expiring_sub_one', { n: expCount })
          : t('page.overview.insight_expiring_sub_plural', { n: expCount }),
    cta: expCount > 0 ? t('page.overview.insight_expiring_cta') : '',
    tone: expCount > 0 ? 'warning' : 'success',
    href: '/dashboard/accounts/contracts?tab=ACTIVE',
  })

  return (
    <div className={classes.grid}>
      {cards.map((c) => (
        <InsightCard key={c.key} card={c} onSelect={() => navigate({ to: c.href as any })} />
      ))}
    </div>
  )
}

function InsightCard({ card, onSelect }: { card: Card; onSelect: () => void }) {
  const toneClass =
    card.tone === 'urgent'
      ? classes.urgent
      : card.tone === 'warning'
        ? classes.warning
        : card.tone === 'success'
          ? classes.success
          : ''
  const iconToneClass =
    card.tone === 'urgent'
      ? classes.urgentIcon
      : card.tone === 'warning'
        ? classes.warningIcon
        : card.tone === 'success'
          ? classes.successIcon
          : ''
  const ctaToneClass = card.tone === 'urgent' ? classes.urgentCta : card.tone === 'warning' ? classes.warningCta : ''

  return (
    <button type="button" className={`${classes.card} ${toneClass}`} onClick={onSelect}>
      <div className={classes.header}>
        <span className={`${classes.icon} ${iconToneClass}`}>{card.icon}</span>
        <span className={classes.title}>{card.title}</span>
      </div>
      <div className={`${classes.value} ${card.value === '0' ? classes.muted : ''}`}>
        {card.value === '0' && (card.tone === 'success' || card.tone === 'neutral') ? '—' : card.value}
      </div>
      <div className={classes.sub}>{card.sub}</div>
      {card.cta && (
        <div className={`${classes.cta} ${ctaToneClass}`}>
          {card.cta}
          <LuArrowRight size={12} />
        </div>
      )}
    </button>
  )
}
